import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { name: "Home", path: "/", testid: "nav-home-link" },
  { name: "Our Story", path: "/story", testid: "nav-story-link" },
  { name: "Menu", path: "/menu", testid: "nav-menu-link" },
  { name: "Book Crazy Buns", path: "/book", testid: "nav-book-link" },
  { name: "Find The Truck", path: "/truck", testid: "nav-truck-link" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 bg-cb-yellow border-b-3 border-cb-black">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group" onClick={() => setOpen(false)}>
          <img
            src="/logo.jpg"
            alt="Crazy Buns logo"
            className="w-11 h-11 rounded-full border-3 border-cb-black object-cover shadow-hard-sm group-hover:rotate-6 transition-transform duration-200"
          />
          <span className="font-display font-bold text-xl sm:text-2xl uppercase leading-none tracking-tight">
            Crazy <span className="text-cb-pink">Buns</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-display font-semibold uppercase text-sm px-3.5 py-2 rounded-full border-2 transition-[background-color,color,transform] duration-150 ${
                  isActive
                    ? "bg-cb-black text-cb-yellow border-cb-black"
                    : "border-transparent hover:border-cb-black hover:bg-white/70 hover:-rotate-1"
                }`
              }
            >
              {l.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/truck"
            data-testid="nav-find-truck-cta"
            className="hidden sm:inline-flex cb-btn cb-btn-pink !px-5 !py-2.5 !text-xs sm:!text-sm"
          >
            <MapPin className="w-4 h-4" /> Find The Truck
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-11 h-11 inline-flex items-center justify-center rounded-full border-3 border-cb-black bg-white shadow-hard-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-[box-shadow,transform]"
          >
            {open ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={`${location.pathname}-mobile`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="lg:hidden overflow-hidden border-t-3 border-cb-black bg-cb-yellow"
            aria-label="Mobile"
          >
            <div className="px-4 py-4 flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display font-semibold uppercase px-4 py-3 rounded-2xl border-3 border-cb-black shadow-hard-sm ${
                      isActive ? "bg-cb-black text-cb-yellow" : i % 2 ? "bg-white" : "bg-cb-teal"
                    }`
                  }
                >
                  {l.name}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
